import { Store } from "@tanstack/react-store";
import { cloneDeep, get, merge, unset } from "lodash";
import { ProductV3 } from "../types";
import { localStorage } from "../utils/local-storage";

// Store for product customizations
export const customizationsStore = new Store<Record<string, Partial<ProductV3>>>({});

// Storage key for customizations
export const CUSTOMIZATIONS_KEY = "customizations";

// Load customizations from localStorage
export const loadCustomizations = (): void => {
  const customizationsJson = localStorage?.getItem(CUSTOMIZATIONS_KEY);
  if (customizationsJson && customizationsJson !== "undefined") {
    try {
      customizationsStore.state = JSON.parse(customizationsJson) as Record<
        string,
        Partial<ProductV3>
      >;
    } catch (e) {
      console.error("Failed to parse customizations:", e);
      customizationsStore.state = {};
    }
  }
};

// Save customizations to localStorage
export const saveCustomizations = (): void => {
  console.log("Saving customizations:", customizationsStore.state);
  localStorage?.setItem(CUSTOMIZATIONS_KEY, JSON.stringify(customizationsStore.state));
};

// Subscribe to changes in the customizations and save to localStorage
customizationsStore.subscribe(() => {
  console.log("customizationsStore updated");
  saveCustomizations();
});

// Add or update a customization for a product
export const addCustomization = (productCode: string, customization: Partial<ProductV3>): void => {
  if (!productCode) return;

  customizationsStore.setState((prevCustomizations) => {
    const existingCustomization = prevCustomizations[productCode] || {};
    return {
      ...prevCustomizations,
      [productCode]: merge({}, existingCustomization, customization),
    };
  });
};

// Remove a customization for a product
export const removeCustomization = (
  productCode: string,
  partialCustomization: Partial<ProductV3>
): void => {
  customizationsStore.setState((prevState) => {
    // If there's no customization for this product, nothing to remove
    if (!prevState[productCode]) return prevState;

    // Clone the existing customization to avoid mutating state directly
    const customization = cloneDeep(prevState[productCode]);

    // Handle nested properties by using lodash unset
    for (const key in partialCustomization) {
      const typedKey = key as keyof Partial<ProductV3>;
      if (
        typeof partialCustomization[typedKey] === "object" &&
        partialCustomization[typedKey] !== null
      ) {
        // For nested objects, unset specific nested keys
        const nestedObj = partialCustomization[typedKey] as Record<string, unknown>;
        for (const nestedKey in nestedObj) {
          unset(customization, `${key}.${nestedKey}`);
        }
        // If the parent object is now empty, remove it entirely
        const parentObj = get(customization, key) as Record<string, unknown> | undefined;
        if (parentObj && Object.keys(parentObj).length === 0) {
          delete customization[typedKey];
        }
      } else {
        // For scalar properties, simply delete them
        delete customization[typedKey];
      }
    }

    // If no customizations left, remove the entire product entry
    if (Object.keys(customization).length === 0) {
      const newState = { ...prevState };
      delete newState[productCode];
      return newState;
    }

    return { ...prevState, [productCode]: customization };
  });
};

// Apply customizations to a product
export const withCustomizations = (product: ProductV3): ProductV3 => {
  if (!product.code) return product;

  const customization = customizationsStore.state[product.code];
  if (!customization) return product;

  // Use lodash merge to deep merge the product with its customizations
  return merge({}, product, customization);
};
